import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { validate } from '@adornis/validation/decorators.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import type { ValidationFunctionOptions } from '@adornis/validation/validator.js';
import { productQuantityWarning } from '@chancenwerk/server/logistikMailTemplates.js';
import { chancenWerkMailer } from '@chancenwerk/server/mailer.js';
import { DateTime } from 'luxon';
import { type UpdateResult } from 'mongodb';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { LogistikOrder, type ChancenBriefVariableValue } from './LogistikOrder.js';
import { LogistikProductFilter } from './LogistikProductFilter.js';
import {
  LogistikAdvertisingMaterialCategory,
  LogistikProductAvailability,
  LogistikProductTargetAudience,
  LogistikSchoolGrade,
  LogistikShippingStatus,
  Services,
  type LogistikLearningMaterialCategory,
} from './enums/LogistikEnums.js';

export type LogistikProductUnion =
  | LogistikProduct
  | LearningMaterial
  | LogistikProductSet
  | AdvertisingMaterial
  | WorkMaterial
  | EventTicket
  | WorkshopPack
  | Furnishing
  | TravelCertificate;

/**
 * @entity LogistikProduct
 * @extends MongoEntity
 */
@Entity()
export class LogistikProduct extends MongoEntity {
  public static override _class = 'HubProduct'; // TODO needs migration before we can change this
  public static override _collectionName = 'hubProducts'; // TODO needs migration before we can change this

  static override async afterUpdate(
    _oldEntity: { _id?: string | undefined; _class?: string | undefined },
    newEntity: LogistikProduct,
    _result: UpdateResult,
  ): Promise<void> {
    const col = await getCollection<LogistikProduct>(this._class);
    await col.updateOne({ _id: newEntity._id }, { $set: { updatedAt: DateTime.now().toJSDate() } });
  }

  @Field(type => DateTime, {
    default: v => v ?? DateTime.now(),
  })
  public createdAt!: DateTime;

  @Field(type => DateTime, {
    default: v => v ?? DateTime.now(),
  })
  public updatedAt!: DateTime;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie einen Titel an`, { key, source });
  })
  @Field(type => String)
  public title!: string;

  @Field(type => String)
  public description!: string;

  @Field(type => String)
  public imageID!: string;

  @Field(type => String)
  public notice: Maybe<string>;

  @Field(type => [String])
  public imageIDs?: string[];

  @Field(type => Int, { default: v => v ?? 1000 })
  public availableQuantity!: number;

  @Field(type => Int, {
    default: v => v ?? 0,
    resolve: function (this: LogistikProduct) {
      return async () => {
        const orderCol = await getRawCollection<LogistikOrder>(LogistikOrder._collectionName);
        return orderCol
          .aggregate([
            {
              $match: {
                'items.productID': this._id,
                status: { $in: [LogistikShippingStatus.COMPLETED, LogistikShippingStatus.SHIPPED] },
              },
            },
            { $unwind: '$items' },
            { $match: { 'items.productID': this._id } },
            { $group: { _id: null, quantity: { $sum: '$items.quantity' } } },
          ])
          .toArray()
          .then((result: [{ _id: null; quantity: number }]) => result[0]?.quantity ?? 0);
      };
    },
  })
  public orderedQuantity!: number;

  @Field(type => Int, { default: v => v ?? 0 })
  public quantityWarningThreshold!: number;

  @Field(type => String)
  public quantityWarningMail!: string;

  @Field(type => String)
  public productId!: string;

  @Field(type => String, { default: v => v ?? LogistikProductAvailability.AVAILABLE })
  public availability!: LogistikProductAvailability;

  @Field(type => Boolean, { default: v => v ?? true })
  public isPublished!: boolean;

  @Field(type => Int, { default: v => v ?? 1 })
  public minOrderAmount!: number;

  @Field(type => Int, { default: v => v ?? 100 })
  public maxOrderAmount!: number;

  @Query(type => [LogistikProduct])
  static getLogistikProductsByIDs(@Arg('ids', type => [String]) ids: string[]) {
    return async (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      const col = await getCollection<LogistikProduct>(this._class);
      const items = await col.find({ _id: { $in: ids } }).toArray();
      return items;
    };
  }

  @Subscription(type => [LogistikProduct])
  static subscribeLogistikProductsPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      if (!filter) return of([] as LogistikProduct[]);
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<LogistikProduct>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            ...filter.getMongoOptions().sort,
            skip,
            limit,
          });
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }

  @Subscription(type => [LogistikProduct])
  static subscribeAllLogistikProductsBySearch(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      if (!filter) return of([] as LogistikProduct[]);
      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<LogistikProduct>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
            projection: { _id: 1 },
          });
        }),
        switchMap(async resultIDs => {
          const col = await getRawCollection<LogistikProduct>(this._collectionName);
          return col
            .aggregate([
              { $match: { _id: { $in: resultIDs.map(x => x._id) } } },

              // get orderedQuantity to enable sorting by it
              {
                $lookup: {
                  from: 'hubOrders',
                  as: 'orderedQuantity',
                  let: { productID: '$_id' },
                  pipeline: [
                    {
                      $match: {
                        $expr: {
                          $and: [
                            { $in: ['$$productID', '$items.productID'] },
                            { $in: ['$status', [LogistikShippingStatus.COMPLETED, LogistikShippingStatus.SHIPPED]] },
                          ],
                        },
                      },
                    },
                    { $unwind: '$items' },
                    { $match: { $expr: { $eq: ['$$productID', '$items.productID'] } } },
                    { $group: { _id: null, quantity: { $sum: '$items.quantity' } } },
                  ],
                },
              },
              {
                $set: {
                  orderedQuantity: {
                    $cond: { if: { $isArray: '$orderedQuantity' }, then: '$orderedQuantity', else: [{ quantity: 0 }] },
                  },
                },
              },
              {
                $set: {
                  orderedQuantity: {
                    $cond: { if: { $size: '$orderedQuantity' }, then: '$orderedQuantity', else: [{ quantity: 0 }] },
                  },
                },
              },
              { $unwind: '$orderedQuantity' },
              { $set: { orderedQuantity: '$orderedQuantity.quantity' } },

              // sorting from filter
              { $sort: filter.getMongoOptions().sort },
              { $skip: skip },
              { $limit: limit },
            ])
            .toArray();
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }

  @Query(type => Boolean)
  public static sendQuantityWarningMail(@Arg('productID', type => String) productID: string) {
    return async (gqlFields: void) => {
      const col = await getCollection<LogistikProduct>(this._class);
      const product = constructValue(await col.findOne({ _id: productID })) as Maybe<LogistikProduct>;
      if (!product) return false;

      try {
        await chancenWerkMailer.sendMail(
          await Message.compose({
            subject: `Warnung: Produkt ${product.title} hat nur noch ${product.availableQuantity} Stück auf Lager`,
            html: productQuantityWarning({ product }),
          }),
          new Recipient(product.quantityWarningMail),
        );
      } catch (e) {
        console.error(e);
      }

      return true;
    };
  }

  @Subscription(type => [LogistikProduct])
  static subscribeAllPublishedLogistikProducts() {
    return (gqlFields: BaseQLSelectionSet<LogistikProduct>) => {
      const handle = getCollectionHandle<LogistikProduct>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle.watchQuery(
            publishFilter
              ? { $and: [publishFilter, { isPublished: { $ne: false } }, { _class: this._class }] }
              : { $and: [{ isPublished: { $ne: false } }, { _class: this._class }] },
            {
              sort: { _id: -1 },
            },
          );
        }),
        catchError(e => {
          return of([] as LogistikProduct[]);
        }),
      );
    };
  }
}

/**
 * @entity LearningMaterial
 * @extends LogistikProduct
 */
@Entity()
export class LearningMaterial extends LogistikProduct {
  public static override _class = 'LearningMaterial';

  @Field(type => String)
  public pdfID!: string;

  @Field(type => String)
  public solutionPDFID: Maybe<string>;

  @Field(type => String)
  public handoutPDFID: Maybe<string>;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie ein Thema an`, { key, source });
  })
  @Field(type => String)
  public topic!: string;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie ein Fach an`, { key, source });
  })
  @Field(type => String)
  public category!: LogistikLearningMaterialCategory;

  @validate(({ value, key, source }) => {
    if (!value || value.length === 0)
      throw new ValidationError(`Bitte geben Sie mindestens eine Jahrgangsstufe an`, { key, source });
    if (!value.every(v => Object.values(LogistikSchoolGrade).includes(v)))
      throw new ValidationError(`Ungültige Jahrgangsstufe`, { key, source });
  })
  @Field(type => [String])
  public grades!: LogistikSchoolGrade[];

  @Field(type => String, { default: v => v ?? null })
  public digitalVariantPDFID: Maybe<string>;
}

@Entity()
export class LogistikProductSet extends LogistikProduct {
  public static override _class = 'LogistikProductSet';

  @validate(({ value, key, source }) => {
    if (!value?.length) throw new ValidationError('Bitte wählen Sie mindestens ein Produkt aus.');
  })
  @Field(type => [SelectedSetProduct], { default: v => v ?? [] })
  public selectedProducts!: SelectedSetProduct[];

  @Field(type => [String], { default: v => v ?? [] })
  public services!: Services[];
}

@Entity()
export class SelectedSetProduct extends AdornisEntity {
  public static override _class = 'SelectedSetProduct';

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(type => String)
  public productID!: string;

  @validate(({ value, key, source }) => {
    if (!value) throw new ValidationError(`Bitte geben Sie eine Anzahl an`, { key, source });
    if (value < 1) throw new ValidationError(`Anzahl muss mindestens 1 sein`, { key, source });
  })
  @Field(type => Int, { default: v => v ?? 1 })
  public quantity!: number;
}

/**
 * @entity AdvertisingMaterial
 * @extends LogistikProduct
 */
@Entity()
export class AdvertisingMaterial extends LogistikProduct {
  public static override _class = 'AdvertisingMaterial';

  // TODO would be nicer to use some kind of trait system or multiple inheritance to get this field into chancenbrief and advertisingMatieral
  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, AdvertisingMaterial>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(type => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @Field(type => String)
  public pdfID?: string;

  @Field(type => String)
  public category!: LogistikAdvertisingMaterialCategory;
}

@Entity()
export class WorkMaterial extends LogistikProduct {
  static override _class = 'WorkMaterial';

  @Subscription(type => [WorkMaterial])
  static subscribePublishedWorkMaterials(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<WorkMaterial>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<WorkMaterial>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: this.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(type => Int)
  static subscribePublishedWorkMaterialsCount() {
    return () => {
      const handle = getCollectionHandle<WorkMaterial>(this._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: this.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}

@Entity()
export class EventTicket extends LogistikProduct {
  static override _class = 'EventTicket';

  @Subscription(type => [EventTicket])
  static subscribePublishedEventTickets(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<EventTicket>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<EventTicket>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: this.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(type => Int)
  static subscribePublishedEventTicketsCount() {
    return () => {
      const handle = getCollectionHandle<EventTicket>(this._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: this.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(tickets => tickets.length));
    };
  }
}

@Entity()
export class WorkshopPack extends LogistikProduct {
  static override _class = 'WorkshopPack';

  @Subscription(type => [EventTicket])
  static subscribePublishedWorkshopPack(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<WorkshopPack>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<WorkshopPack>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: this.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(type => Int)
  static subscribePublishedWorkshopPackCount() {
    return () => {
      const handle = getCollectionHandle<WorkshopPack>(this._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: this.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(tickets => tickets.length));
    };
  }
}

@Entity()
export class Furnishing extends LogistikProduct {
  static override _class = 'Furnishing';

  @Subscription(type => [Furnishing])
  static subscribePublishedFurnishings(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Furnishing>) => {
      const selector = filter?.getMongoFilter();
      const handle = getCollectionHandle<Furnishing>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: this.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(type => Int)
  static subscribePublishedFurnishingsCount() {
    return () => {
      const handle = getCollectionHandle<Furnishing>(this._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: this.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}

export const possiblePrefillChancenBriefVariableNames = [
  'Schule_Name',
  'Schule_Strasse',
  'Schule_Hausnummer',
  'Schule_PLZ',
  'Schule_Stadt',
  'Schule_Kuerzel',
  'Wochentag_1',
  'Wochentag_2',
  'Wochentag_3',
  'Lernförderzeit_1',
  'Lernförderzeit_2',
  'Lernförderzeit_3',
  'Raumnummer',
  'Name_Schulkoordination',
  'Mobilnummer',
  'email1',
  'email2',
  'Name',
] as const;

export enum DocxVariableType {
  TEXT = 'Text',
  QR_CODE = 'QR-Code',
}

@Entity()
export class ChancenBriefVariable extends AdornisEntity {
  static override _class = 'FLYAVariable'; // TODO need to add migration to update this

  @Field(type => ID, { default: v => v ?? A.getGloballyUniqueID() })
  _id!: string;

  @validate(nonOptional())
  @Field(type => String)
  variableName!: string;

  @Field(type => String, { default: v => v ?? DocxVariableType.TEXT })
  type!: DocxVariableType;

  @Field(type => Int, { default: v => v ?? 150 })
  width!: number;

  @Field(type => Int, { default: v => v ?? 150 })
  height!: number;
}

@Entity()
export class TravelCertificate extends LogistikProduct {
  static override _class = 'TravelCertificate';

  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, ChancenBrief>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(type => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @validate(nonOptional())
  @Field(type => ID)
  flyaDocumentID!: string;

  @Field(type => [ChancenBriefVariable], { default: v => v ?? [] })
  flyaVariables: Maybe<ChancenBriefVariable[]>;

  getGeneratedPDFLink(chancenBriefVariableValues: ChancenBriefVariableValue[]) {
    return {
      path: `/api/serve-travel-certificate/${this._id}/${encodeURIComponent(`${this.title}.pdf`)}`,
      searchParams: new URLSearchParams({
        variables: JSON.stringify(chancenBriefVariableValues.map(variable => variable.toJSON())),
      }),
    };
  }
}

@Entity()
export class ChancenBrief extends LogistikProduct {
  static override _class = 'FLYA'; // TODO need to add migration to update this

  // TODO would be nicer to use some kind of trait system or multiple inheritance to get this field into chancenbrief and advertisingMatieral
  @validate(({ value, key, source }: ValidationFunctionOptions<Maybe<string[]>, ChancenBrief>) => {
    if (!value)
      throw new ValidationError(`Bitte geben Sie eine Zielgruppe an oder wählen Sie "Alle" aus`, { key, source });
    for (const valueEntry of value) {
      if (!Object.values(LogistikProductTargetAudience).includes(valueEntry as LogistikProductTargetAudience))
        throw new ValidationError(
          `Es können nur vordefinierte Werte für die Zielgrupppe genutzt werden! (gegeben wurde: ${valueEntry})`,
        );
    }
  })
  @Field(type => [String])
  targetAudiences?: Maybe<LogistikProductTargetAudience[]>;

  @validate(nonOptional())
  @Field(type => ID)
  flyaDocumentID!: string;

  @Field(type => [ChancenBriefVariable], { default: v => v ?? [] })
  flyaVariables: Maybe<ChancenBriefVariable[]>;

  getGeneratedPDFLink(chancenBriefVariableValues: ChancenBriefVariableValue[]) {
    return {
      path: `/api/serve-chancenbrief/${this._id}/${encodeURIComponent(`${this.title}.pdf`)}`,
      searchParams: new URLSearchParams({
        variables: JSON.stringify(chancenBriefVariableValues.map(variable => variable.toJSON())),
      }),
    };
  }

  @Subscription(type => [Furnishing])
  static subscribePublishedChancenBriefe(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => LogistikProductFilter) filter?: LogistikProductFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<Furnishing>) => {
      const selector = filter?.getMongoFilter() ?? {};
      const handle = getCollectionHandle<Furnishing>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(pF =>
          handle.watchQuery({
            $and: [
              { _class: { $in: this.validSubclasses() } },
              { isPublished: { $ne: false } },
              selector,
              ...(pF ? [pF] : []),
            ],
          }),
        ),
      );
    };
  }

  @Subscription(type => Int)
  static subscribePublishedChancenBriefeCount() {
    return () => {
      const handle = getCollectionHandle<Furnishing>(this._collectionName);
      return handle
        .watchQuery(
          { _class: { $in: this.validSubclasses() }, isPublished: { $ne: false } },
          { projection: { _id: 1 } },
        )
        .pipe(map(furnishings => furnishings.length));
    };
  }
}
